import { useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export function DatePicker({
  value,
  onChange,
  blocked,
  hasError,
  testid,
  placeholder = "Choisir une date",
  allowPast = false,
  minDaysAhead = 0,
  markedDays = [],
  dayStatus = {},
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(`${value}T12:00:00`) : undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + minDaysAhead);

  const disabled = [
    ...(allowPast && !minDaysAhead ? [] : [{ before: minDate }]),
    ...blocked.dates.map((d) => new Date(`${d}T12:00:00`)),
    ...(blocked.weekdays.length ? [{ dayOfWeek: blocked.weekdays }] : []),
    ...(blocked.months.length
      ? [(date) => blocked.months.includes(date.getMonth() + 1)]
      : []),
  ];

  const DOT = {
    normal: "after:bg-[#D4AF37]",
    low: "after:bg-[#D97706]",
    full: "after:bg-red-600",
    privatised: "after:bg-[#102C1F]",
  };
  const statusGroups = { normal: [], low: [], full: [], privatised: [] };
  Object.entries(dayStatus).forEach(([d, s]) => {
    if (statusGroups[s]) statusGroups[s].push(new Date(`${d}T12:00:00`));
  });
  markedDays.forEach((d) => {
    if (!dayStatus[d]) statusGroups.normal.push(new Date(`${d}T12:00:00`));
  });
  const dotCls = (s) =>
    `relative after:absolute after:bottom-0.5 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full ${DOT[s]} after:content-['']`;
  const hasStatus = Object.keys(dayStatus).length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`flex w-full items-center gap-2 rounded-sm border px-4 py-2.5 text-left text-sm outline-none transition-colors duration-300 ${
            hasError
              ? "border-red-600 bg-red-50"
              : "border-[#102C1F]/25 bg-[#F4F1EB] hover:border-[#102C1F]"
          } ${value ? "text-[#102C1F]" : "text-[#102C1F]/40"}`}
        >
          <CalendarDays size={16} className="shrink-0 opacity-60" />
          {selected ? format(selected, "dd/MM/yyyy") : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`${testid}-calendar`}
        className="z-[90] w-auto border-[#102C1F]/15 bg-[#F4F1EB] p-0 text-[#102C1F]"
        align="start"
      >
        <Calendar
          mode="single"
          locale={fr}
          selected={selected}
          onSelect={(d) => {
            if (!d) return;
            onChange(format(d, "yyyy-MM-dd"));
            setOpen(false);
          }}
          disabled={disabled}
          modifiers={statusGroups}
          modifiersClassNames={{
            normal: dotCls("normal"),
            low: dotCls("low"),
            full: dotCls("full"),
            privatised: dotCls("privatised"),
          }}
          initialFocus
        />
        {hasStatus && (
          <div
            data-testid={`${testid}-legend`}
            className="flex flex-wrap gap-x-3 gap-y-1 border-t border-[#102C1F]/10 px-3 py-2"
          >
            {[
              ["normal", "Réservations"],
              ["low", "Peu de places"],
              ["full", "Complet"],
              ["privatised", "Privatisé"],
            ].map(([s, label]) => (
              <span
                key={s}
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] text-[#102C1F]/60"
              >
                <span
                  className={`inline-block h-2 w-2 rounded-full ${DOT[s].replace("after:", "")}`}
                />
                {label}
              </span>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
